import React, { useState } from "react";
import IntegrationDataMapping from "../shared/IntegrationDataMapping";
import { getScopeOptions } from "./ShopifyScopeOptions";

// ---------------------------------------------------------------------------
// Guide sub-components
// ---------------------------------------------------------------------------

type GuideSection = "overview" | "agent-panel" | "workflow-data" | "ai-tools";

const GUIDE_SECTIONS: { id: GuideSection; title: string }[] = [
  { id: "overview",       title: "What is field mapping?" },
  { id: "agent-panel",    title: "Agent CRM panel" },
  { id: "workflow-data",  title: "HTTP POST / workflows" },
  { id: "ai-tools",       title: "AI tools (no mapping)" },
];

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-primary-bg border border-cornflower-blue-200 rounded-lg p-3 mt-3 text-xs text-cornflower-blue-800 leading-relaxed">
      {children}
    </div>
  );
}

function TermBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3 text-xs text-slate-600 leading-relaxed">
      {children}
    </div>
  );
}

function InlineCode({ children }: { children: string }) {
  return (
    <code className="bg-white px-1 py-0.5 rounded border border-slate-200 text-cornflower-blue-800 font-mono text-[11px]">
      {children}
    </code>
  );
}

function GuideOverview() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">What is Field Mapping?</h3>
      <p className="mb-3">
        Field mapping tells the platform <strong>which Shopify data to surface in the agent CRM sidebar</strong> and what to call it. When an agent opens a conversation, the sidebar shows customer and order information pulled from Shopify — field mapping controls exactly which fields appear and their display labels.
      </p>
      <p className="mb-3">
        Think of it as a <em>view configuration</em>: you pick a Shopify object type (Customer, Order, Product), choose the fields you care about, give them friendly labels, and decide whether agents can search on them.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-2">Quick decision guide</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Use case</th>
              <th className="px-3 py-2 text-left">Field mapping needed?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-2">Show customer data in the agent sidebar</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — required</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Pass Shopify data into an HTTP POST workflow node</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — required</td>
            </tr>
            <tr>
              <td className="px-3 py-2">AI responds to visitor questions about orders/products</td>
              <td className="px-3 py-2 text-slate-500 font-medium">No — AI reads raw data</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">NetSuite lead capture or case creation</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — maps chat fields to NS fields</td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>Scope type</strong> — Shopify data is grouped into object types: Customer, Order, Product. Each mapping panel targets one scope type. You can add multiple panels to map fields from different object types.
      </TermBox>
    </div>
  );
}

function GuideAgentPanel() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">Agent CRM Panel</h3>
      <p className="mb-3">
        When an agent handles a conversation, the right-side CRM panel displays a contact's linked Shopify data. The fields shown, their order, and their labels all come from this mapping configuration.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Field options explained</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Option</th>
              <th className="px-3 py-2 text-left">Effect</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-2 font-medium">Label</td>
              <td className="px-3 py-2">The display name agents see. Rename <InlineCode>total_price</InlineCode> to "Order Total".</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2 font-medium">Show in search</td>
              <td className="px-3 py-2">Agents can search conversations by this field value (e.g. search by order number).</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">Show when selected</td>
              <td className="px-3 py-2">Field appears in the contact detail view when the agent drills into a record.</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2 font-medium">Default value</td>
              <td className="px-3 py-2">Fallback text when the Shopify field is empty or null.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox>
        <strong>Tip:</strong> Map only the fields your agents actually need to take action. A contact card with 20 fields is harder to use than one with 5 focused fields like order status, total, and last order date.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Customer mapping</h4>
      <p className="text-xs text-slate-600 mb-1">A typical customer panel maps these Shopify fields:</p>
      <CodeBlock>{`Shopify Field          Label                 Search  Show
──────────────────────────────────────────────────────────
email                  Email                 ✓       ✓
first_name             First Name            ✓       ✓
last_name              Last Name             ✓       ✓
orders_count           Total Orders                  ✓
total_spent            Lifetime Value                ✓
tags                   Customer Tags                 ✓`}</CodeBlock>
    </div>
  );
}

function GuideWorkflowData() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">HTTP POST / Workflow Data Passing</h3>
      <p className="mb-3">
        In a workflow, an <strong>HTTP POST node</strong> can call an external API (your CRM, a webhook, a ticketing system) and include Shopify data in the request body. To pass a Shopify field value, you first map it — that gives it a variable name — then reference that variable in the POST node.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">How the variable is formed</h4>
      <p className="text-xs text-slate-600 mb-2">
        Once you map a Shopify field, it becomes available as a workflow variable. The variable name follows the pattern <InlineCode>{"{shopify.scopeType.fieldName}"}</InlineCode>:
      </p>
      <CodeBlock>{`Scope: Order  →  Field: fulfillment_status
Variable: {shopify.order.fulfillment_status}

Scope: Customer  →  Field: email
Variable: {shopify.customer.email}`}</CodeBlock>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Notify a ticketing system</h4>
      <p className="text-xs text-slate-600 mb-1">
        This workflow HTTP POST node sends a support ticket to an external API, including the customer's email and their most recent order ID from Shopify:
      </p>
      <CodeBlock>{`// HTTP POST node — request body
{
  "contact_email": "{shopify.customer.email}",
  "order_id":      "{shopify.order.name}",
  "status":        "{shopify.order.fulfillment_status}",
  "channel":       "live-chat",
  "conversation":  "{conversation.id}"
}`}</CodeBlock>

      <InfoBox>
        <strong>Field mapping is the bridge.</strong> Without a mapping entry for <InlineCode>order.name</InlineCode>, the variable <InlineCode>{"{shopify.order.name}"}</InlineCode> would be empty at runtime. Map first, then reference in the POST node.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Lead capture workflow</h4>
      <p className="text-xs text-slate-600 mb-1">
        Collect visitor information via a workflow form and write it back to your system alongside Shopify context:
      </p>
      <CodeBlock>{`// HTTP POST — lead capture + Shopify context
{
  "lead": {
    "name":          "{visitor.name}",
    "email":         "{visitor.email}",
    "shopify_email": "{shopify.customer.email}",
    "lifetime_value":"{shopify.customer.total_spent}",
    "last_order":    "{shopify.order.name}"
  }
}`}</CodeBlock>
    </div>
  );
}

function GuideAiTools() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">AI Tools — No Field Mapping Required</h3>
      <p className="mb-3">
        The four built-in Shopify AI tools (<InlineCode>shopify_get_customer</InlineCode>, <InlineCode>shopify_search_products</InlineCode>, <InlineCode>shopify_get_order</InlineCode>, <InlineCode>shopify_get_recent_orders</InlineCode>) work completely differently from field mapping. They don't use the mapping configuration at all.
      </p>

      <h4 className="font-medium text-slate-700 mt-3 mb-1">How AI tools work</h4>
      <p className="text-xs text-slate-600 mb-2">
        When an AI workflow node runs, it calls the Shopify GraphQL Admin API at runtime and receives the full raw JSON response. The AI model then uses natural language understanding to extract the relevant details and compose a response for the visitor — no schema configuration needed.
      </p>
      <CodeBlock>{`// What the AI receives internally (not visible to visitor)
{
  "customer": {
    "id": "gid://shopify/Customer/123",
    "email": "jane@example.com",
    "displayName": "Jane Smith",
    "numberOfOrders": 7,
    "amountSpent": { "amount": "1204.50", "currencyCode": "USD" },
    "orders": { "edges": [ ... ] }
  }
}

// What the AI says to the visitor
"Hi Jane! I can see you've placed 7 orders with us.
 Your most recent order (#1094) was shipped on Jan 28th
 via UPS — tracking number 1Z999AA10123456784."`}</CodeBlock>

      <InfoBox>
        <strong>Why no mapping?</strong> Field mapping is a <em>static display config</em> — you define once what to show in the sidebar. AI tools are <em>dynamic runtime calls</em> — the AI decides which fields matter based on the visitor's question. Mapping 50 fields doesn't help the AI answer "where is my order?" any better than receiving the raw JSON directly.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Comparison: AI tool vs mapped field</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs mt-2">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Capability</th>
              <th className="px-3 py-2 text-left">AI Tool</th>
              <th className="px-3 py-2 text-left">Field Mapping</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs">
            <tr>
              <td className="px-3 py-2">Agent sidebar display</td>
              <td className="px-3 py-2 text-slate-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Visitor-facing AI answers</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
              <td className="px-3 py-2 text-slate-400">No</td>
            </tr>
            <tr>
              <td className="px-3 py-2">HTTP POST variable injection</td>
              <td className="px-3 py-2 text-slate-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Requires configuration</td>
              <td className="px-3 py-2 text-slate-500">No (auto-activated)</td>
              <td className="px-3 py-2 text-slate-500">Yes (this panel)</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Reads live Shopify data</td>
              <td className="px-3 py-2 text-success-green-700">Yes (at query time)</td>
              <td className="px-3 py-2 text-success-green-700">Yes (at load time)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>When you need both:</strong> An AI workflow node can answer "where is my order?" without any mapping. But if the same workflow also sends a POST to your ticketing system, you need field mapping to supply the <InlineCode>{"{shopify.order.name}"}</InlineCode> variable that goes into the POST body. Use AI tools for conversation; use field mapping for data extraction and display.
      </TermBox>
    </div>
  );
}

function FieldMappingGuide() {
  const [guideSection, setGuideSection] = useState<GuideSection>("overview");
  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-4">
      <div className="flex">
        {/* Sidebar nav */}
        <nav className="w-48 shrink-0 border-r border-slate-200 p-3" aria-label="Field mapping guide sections">
          <div className="space-y-0.5">
            {GUIDE_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setGuideSection(s.id)}
                className={`block w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${
                  guideSection === s.id
                    ? "bg-primary-bg text-primary-active font-medium"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 p-5 text-sm text-slate-700 leading-relaxed overflow-y-auto max-h-[520px]">
          {guideSection === "overview"      && <GuideOverview />}
          {guideSection === "agent-panel"   && <GuideAgentPanel />}
          {guideSection === "workflow-data" && <GuideWorkflowData />}
          {guideSection === "ai-tools"      && <GuideAiTools />}
        </div>
      </div>
    </div>
  );
}

const SCOPE_OPTIONS = getScopeOptions();

interface Props {
  teamId: number;
  selectedConfigId: number;
}

export default function ShopifyDataMapping(props: Props) {
  return (
    <IntegrationDataMapping
      selectedConfigId={props.selectedConfigId}
      teamId={props.teamId}
      description="Configure which Shopify fields appear in the agent CRM sidebar and as workflow variables."
      guide={<FieldMappingGuide />}
      guideLabel="Field Mapping Guide ▾"
      fieldKey="ShopifyField"
      fieldLabel="Shopify Field"
      apiPrefix="Shopify/Integrations"
      getFieldsPath="GetShopifyFields"
      scopeOptions={SCOPE_OPTIONS}
    />
  );
}
